import { computed, inject, ref, watch, onUnmounted, nextTick  } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { inBrowser } from '@kit/utils/EnvironmentHelper'
import { addEvent, removeEvent } from '@kit/utils/EventBus'
import { sleep } from '@kit/utils/Sleep'

/////////////////////////////////////////////////////////////////////
//
//  A self-contained setup function for use with sortable tables.
// 
/////////////////////////////////////////////////////////////////////



export const paginationSetup = ({
  urlSlug,          //job-openings
  mainPrefetchKey,  //jobs
  totalPrefetchKey, //totalJobs
  description,      //the description function. takes jobs, routePage.value, numberOfPages.value 
  title,            //the title function: //the description function. takes jobs, routePage.value, numberOfPages.value 
  numberPerPage,
}) => {

    const route = useRoute()
    const router = useRouter()
    const currentPage = ref(0)
    const hydrate = inject("hydrate")

    const { prefetch, refresh } = hydrate({
      prefetch:[{
        name:mainPrefetchKey,
        params() {
          return {
            page: routePage.value-1,
            numberPerPage
          }
        },
        cache(params) {
          return params.page
        }
      },totalPrefetchKey],
      mounted() {
        currentPage.value = routePage.value
      },
      metadata(meta, prefetchDataObj) {

        const jobs = prefetchDataObj[mainPrefetchKey]
        let descriptionString = description ? description(jobs, routePage.value, numberOfPages.value) : ""
        let titleString = title ? title(jobs, routePage.value, numberOfPages.value) : ""
        

        meta.addCanonical()
        meta.addTitle(titleString)
        meta.addMetaItem([
          { name:'description', content: descriptionString },
          { name:'og:title', content: titleString },
          { name:'og:description', content: descriptionString },
        ])

      }
    })

  
    //This works
    const paginationUpdateHandler = (data) => {
      currentPage.value = data
    }
    const routePage = computed(() => {
      const page = parseInt(route.params.page || '1')
      return page
    })
    const numberOfPages = computed(() => {
      const num = prefetch.value ? Math.ceil( parseInt(prefetch.value[totalPrefetchKey]) / numberPerPage ) : 1
      return isNaN(num) ? 1 : num
    })
    const page = computed(() => {
      return prefetch.value ? prefetch.value[mainPrefetchKey] : null
    })
    const total = computed(() => {
      return prefetch.value ? parseInt(prefetch.value[totalPrefetchKey]) : 0
    })
  
    watch(currentPage, (newVal, oldVal) => {
      if(route.name == urlSlug) {
        router.push(`/${urlSlug}/${newVal}`)
      }
    })
    watch(routePage, (_newPage) => {
      refresh()
      currentPage.value = routePage.value
    })

    return { 
      prefetch,
      refresh,
      page, 
      total, 
      routePage,
      currentPage,
      numberOfPages, 
      paginationUpdateHandler,
    }

}




/*---------------------------------------------------
This is for one of the most common use-cases, which is a page
that has to retrieve its content from the api based on a url-slug.

  In your routes.js:
  Make the url to the page. Note that we're making url parameter names "slug"

  {
    name: 'family',
    path: '/family/:slug',
    component: () => import(/asterisk webpackChunkName: "root" asterisk/'@pages/Family.vue'),
    meta: {
      navColors: {
        navText: "white-green",
        navOptions: "deep-grey-green"
      }
    }
  },


  In your api v1.js, List the api connection.

  export const family = async(slug) => {
    return get(`family/slug/${slug}`)
  }


  In project/prefetch.js
  Make the above connection a prefetch task.

  import { family } from '@project/api/flowers/v1'

  async family(slug) {
    return await family(slug)
  },


  In the page component:

  setup() {

    const { id, prefetch } = slugPageSetup({
      routeParam: 'slug',
      prefetchKey: 'family',
      metadata(family, meta) {
        //...do the metadata stuff here.
      }

    })

    return { 
      id,  //make sure that you're returning the id object. very easy to forget this.
      prefetch, 
      //whatever else you need here.
    }
  },


*/
export const slugPageSetup = ({

  routeParam,       // required. id. or whatever the parameter name you've placed in the route info for this page.
                    // This will be the parameter that is fed to your prefetch data. 
                    // In other words, the slug of the story that you're retrieving from the news-story
                    // endpoint on the server. 

  prefetchKey,      // required. The key to the prefetch data used for the page,

  prefetchExtra,    // optional. Array of extra items to go into the prefetch array.

  mounted,          // optional. The mounted hook.

  metadata,         // The callback to add metadata to the page. 
                    // The callback is like this:
                    //
                    //   (dataForPage, meta) {

                    //      meta.addTitle('WeCare | '+contactName.value)
                    //      meta.addMetaItem([
                    //        { name:'description', content: description },
                    //        { name:'og:title', content: title },
                    //        { name:'og:description', content: description }
                    //      ])
                    //
                    //     if(contact.image) {
                    //       meta.addMetaItem([
                    //         { name:"og:image", content: contact.image },
                    //         { name:"og:image:alt", content: `Image of ${title}` },
                    //       ])
                    //     }
                    //
                    //  }



}) => {

  const route = useRoute()
  const id = computed(() => route.params[routeParam] )
  const hydrate = inject('hydrate')
  const exPrefetch = prefetchExtra || []

  const { prefetch, refresh } = hydrate({
    prefetch:[
      { 
        name:prefetchKey, 
        params() { 
          return id.value 
        },
        cache(theId) { 
          return theId 
        },
        errorTo404(data, id, params) {
          return "No page found for "+params
        }
      },
      ...exPrefetch
    ],
    mounted: mounted,
    metadata(meta, pdata) {

      const pdataForThisPage = pdata[prefetchKey]

      meta.addCanonical()

      metadata(pdataForThisPage, meta)

    }
  })

  watch(id, (newVal, oldVal) => {
    if(newVal) {
      refresh() 
    }
  })



  return { 
    id, prefetch
  }

}






// This is a self-contained setup function which enables 
// scrollto-sections that are wired to a url parameter.
// 
// Note that the route must be configured in a certain way.
// It must have the paramsMappedToMultipleSections set to true.
// 
// Note that path has the :id, and so "id" is the property on the params
// that this function looks for. Hence. "id" is the routeParam.

// {
//   name: 'health-services',
//   path: '/services/:id',
//   component:() => import(/* webpackChunkName: "health-services" */'@pages/HealthServices.vue'),
//   meta: {
//     paramsMappedToMultipleSections:true,
//     sitemap: {
//       slugs: async() => {
//         return await slugs('health-services')
//       }
//     }
//   }
// },

export const scrollToSectionSetup = ({
  routeParam,      // id. or whatever the parameter name you've placed in the route info for this page.
  
  prefetchKey,     // The key to the prefetch data used for the page
  
  getSections,      // retrieve the array of sections from the prefetch data
  
  compareSection,  // compare a section to the route param to see if it matches
                   // (used to see what metadata should be applied)
                   //  
                   // The callback is like this:
                   //
                   //  (id, sectionObj) {
                   //    return sectionObj.slug == id 
                   //  }
                   //
  sectionMeta      // The callback used to create the metadata.
                   // The callback is like this:
                   //
                   //  (sectionObj, meta) {
                   //
                   //    meta.addTitle("WeCare | "+service.display_title)
                   //    meta.addMetaItem([
                   //      { name:'description', content: service.description },
                   //      { name:'og:title', content: service.display_title },
                   //      { name:'og:description', content: service.description },
                   //      { name:'og:image', content: pathname(service.image.guid) },
                   //      { name:'og:image:alt', content: "Image for "+service.display_title } 
                   //    ])
                   //  }
                   //
                   //
                   //
}) => {

    const smoothScroll = inject('smoothScroll')
    const hydrate = inject('hydrate')
    const route = useRoute()
    const go = ref(false)
    let d0 = null
    let scrollInMotion = false
    let scrollCache = null

    const id = computed(() => {
      return route.params[routeParam]
    })

    const { prefetch, refresh } = hydrate({
      prefetch:[prefetchKey],

      //Listen for the navigateToCurrentPage event. This is what happens
      //when the user clicks on a nav-menu item for the current page. It gives
      //us a chance to reset the UI. In this case, we have to scroll to the 
      //current place on the page corresponding to the route params.
      mounted() {
        go.value = true
        scrollInMotion = false
        scrollCache = null
        d0 = addEvent('navigateToCurrentPage',() => {
          performScroll(id.value)
        })
        performScroll(id.value)
      },
      metadata(meta, prefetchData) {

        const pdataForThisPage = prefetchData[prefetchKey]

        meta.addCanonical()

        //const services = common.services
        const services = getSections(pdataForThisPage)
        const idVal = id.value

        for(let i=0; i<services.length; i++) {
          const service = services[i]
          if(compareSection(service, idVal)) {

            sectionMeta(service, meta)

            break
          }
        }

      }
    })

    watch(id, (newVal) => {
      if(newVal) {
        refresh()
        performScroll(id.value)
      }
    })



    const _performScroll = async(id) => {
      await nextTick()    
      const el = document.getElementById(id)
      if(el) {
        smoothScroll({
          scrollTo: el,
        })
      }
      await sleep(500)
    }

    const performScroll = async(id) => {

      if(inBrowser) {
        if(scrollInMotion) {
          scrollCache = id
        } else {
          scrollInMotion = true
          await _performScroll(id)
          scrollInMotion = false
          if(scrollCache) {
            const next = scrollCache
            scrollCache = null 
            performScroll(next)
          }
        }
      }
   

    }

    onUnmounted(() => {
      removeEvent(d0)
    })

    return { id, prefetch, refresh }
  }
